import React, { useEffect, useState } from "react";
import { getCookie, logAction } from "./log.js";
import folderIcon from '../assets/folder.png';
import zoomIn from '../assets/zoom.png';
import zoomOut from '../assets/dezoom.png';
import loadingGif from '../assets/loading.gif';
import '../css/PopUpCopyObject.css';
import { Folder } from 'iconoir-react';

const PopUpCopyObject = ({ object, dossier_id, rootFolder, onClose, onConfirm }) => {
    /**
     * treeData : état local qui contient l'arborescence des dossiers/fichiers
     * récupérée depuis l'API. C'est un tableau d'objets représentant l'arborescence adaptée.
     */
    const [treeData, setTreeData] = useState(null);

    /**
     * zoom : état local pour gérer le niveau de zoom de l'affichage de l'arborescence.
     * Valeur numérique représentant le facteur d'échelle (ex: 1 = 100%).
     */
    const [zoom, setZoom] = useState(1);

    /**
     * openFolders : état local sous forme d'objet, clé = chemin d'un dossier,
     * valeur booléenne indiquant si le dossier est ouvert (true) ou fermé (false).
     */
    const [openFolders, setOpenFolders] = useState({});

    /**
     * selectedPath : état local qui contient le chemin complet du dossier sélectionné
     * par l'utilisateur pour la destination de la copie.
     */
    const [selectedPath, setSelectedPath] = useState(null);

    /**
     * API_URL : URL de base de l'API, provenant des variables d'environnement,
     * utilisée pour effectuer les requêtes fetch.
     */
    const API_URL = process.env.REACT_APP_API_URL;

    /**
     * adaptNode : fonction utilitaire qui transforme un objet 'node' brut reçu
     * de l'API en un format simplifié et standardisé pour faciliter l'affichage.
     * @param {Object} node - Un nœud brut contenant nom, type et children.
     * @returns {Object} - Nœud adapté avec propriétés : name, type, children.
     */
    const adaptNode = (node) => ({
        name: node.nom,
        type: node.type,
        children: node.children ? node.children.map(adaptNode) : []
    });

    /**
     * useEffect : au montage du composant ou au changement de dossier_id / object,
     * récupère l'arborescence via l'API, adapte les données et les stocke dans treeData.
     */
    useEffect(() => {
        const fetchTree = async () => {
            try {
                const res = await fetch(`${API_URL}/api/getArborescence.php`, {
                    method: "POST",
                    credentials: 'include',
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        dossier_id: object?.id,
                        repertoire: rootFolder,
                    }),
                });
                const data = await res.json();

                // Selon la réponse backend, adapte les données au format attendu
                const tree = data.tree || data;

                if (tree && Array.isArray(tree)) {
                    const adaptedTree = tree.map(adaptNode);
                    setTreeData(adaptedTree);
                } else {
                    setTreeData(null);
                }
            } catch (err) {
                console.error("Erreur lors du chargement de l'arborescence :", err);
            }
        };

        fetchTree();
    }, [dossier_id, object]);

    /**
     * toggleFolder : bascule l'état ouvert/fermé d'un dossier dans openFolders
     * @param {string} path - Chemin complet du dossier à basculer.
     */
    const toggleFolder = (path) => {
        setOpenFolders(prev => ({
            ...prev,
            [path]: !prev[path]
        }));
    };

    /**
     * renderNode : fonction récursive qui génère le rendu JSX d'un nœud
     * de l'arborescence (dossier ou fichier), avec gestion du clic,
     * sélection et affichage conditionnel des enfants.
     * @param {Object} node - Nœud à afficher (avec name, type, children).
     * @param {string} path - Chemin complet accumulé jusqu'au nœud actuel.
     * @returns {JSX.Element} - Élément JSX représentant le nœud.
     */
    const renderNode = (node, path = "") => {
        const safeName = node.name.replace(/\\/g, "/");
        const currentNodePath = path ? `${path}/${safeName}` : safeName;
        const isOpen = openFolders[currentNodePath];

        return (
            <div key={currentNodePath} className={`folder-block${isOpen ? " open" : ""}`}>
                <div
                    className={`folder-name${node.type !== "D" ? " file" : ""} ${selectedPath === currentNodePath ? "selected" : ""}`}
                    onClick={() => {
                        if (node.type === "D") {
                            toggleFolder(currentNodePath);
                        }
                        setSelectedPath(currentNodePath);
                    }}
                >
                    {/* <img src={folderIcon} width="18" height="18" alt="folder" /> */}
                    <Folder width="18" height="18" />
                    <span>{node.name}</span>
                </div>
                {isOpen && node.children?.length > 0 && (
                    <div className="folder-content">
                        {node.children.map(child => renderNode(child, currentNodePath))}
                    </div>
                )}
            </div>
        );
    };

    /**
     * handleConfirm : envoie une requête PUT pour dupliquer l'objet
     * vers le chemin sélectionné. Log l'action si succès, affiche alert en cas d'erreur.
     */
    const handleConfirm = async () => {
        if (!selectedPath) {
            alert("Veuillez sélectionner un dossier pour la duplication.");
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/modifyObject.php`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dossier: object.id,
                    old: object.path,
                    new: `${selectedPath}\\${object.name}`,
                    action: "copy",
                    type: object.type
                }),
                credentials: 'include'
            });

            const data = await res.json();
            if (res.ok) {
                console.log("Fichier dupliqué avec succès :", data);
                logAction({
                    action: "copyFile",
                    dossierId: object.id,
                    utilisateurId: getCookie("userId"),
                    fichier: object.name,
                    path: `${selectedPath}\\${object.name}`,
                });
                console.log("Action de duplication enregistrée dans les logs.");
            } else {
                console.error("Erreur API :", data);
                alert("Erreur lors du dupliqué du fichier.");
            }
        } catch (err) {
            console.error("Erreur réseau :", err);
            alert("Erreur réseau lors du dupliqué.");
        }
    };

    return (
        <div className="popupCopy-overlay">
            <div className="popupCopy-content move-file">
                <h3>Déplacer le fichier</h3>

                {/* <div className="zoom-controls">
                    <button onClick={() => setZoom(z => Math.min(z + 0.1, 2))}>
                        <img src={zoomIn} alt="Zoom In" />
                    </button>
                    <button onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))}>
                        <img src={zoomOut} alt="Zoom Out" />
                    </button>
                    <button onClick={() => setZoom(1)}>🔄 Reset</button>
                </div> */}

                <div className="folderCopy-structure-wrapper">
                    <div className="folderCopy-structure">
                        <div
                            className="folderCopy-content-inner"
                            style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
                        >
                            {treeData ? (
                                treeData.map(node => renderNode(node))
                            ) : (
                                <div className="loadingCopy-container">
                                    <img src={loadingGif} alt="Chargement..." className="loading-gif" />
                                    <p>Chargement...</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {selectedPath && (
                    <div className="selectedCopy-path">
                        <strong>Destination :</strong> {selectedPath}/{object.name}
                    </div>
                )}

                <div className="flex justify-end my-2 -popupCopy-buttons">
                    <button  className="btn btn-success mr-2" onClick={handleConfirm} disabled={!selectedPath}>
                        Confirmer
                    </button>
                    <button className="btn" onClick={onClose}>Annuler</button>
                </div>
            </div>
        </div>
    );
};

export default PopUpCopyObject;
